import { Component, OnInit, ViewChild, signal, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService, SortMeta } from 'primeng/api';
import { MaterialService } from '../../services/material/material.service';
import { RubroService } from '../../services/rubro/rubro.service';
import { TipoMaterialService } from '../../services/tipo-material/tipo-material.service';
import { TarifaService } from '../../services/tarifa/tarifa.service';
import { DialogService } from '../../shared/services/dialog.service';
import { Material } from '../../domain/material.domain';
import { environment } from '../../../environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { HttpParams } from '@angular/common/http';

interface Column {
  field: string;
  header: string;
  fieldType?: 'text' | 'boolean' | 'boolean-icon' | 'number' | 'date';
  sortable?: boolean;
  exportable?: boolean;
  filtrable?: boolean;
  modeDisplayFilter?: 'menu' | 'row';
}

@Component({
  selector: 'app-materiales',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule,
    RippleModule,
    TooltipModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './materiales.component.html',
  styleUrls: ['./materiales.component.scss']
})
export class MaterialesComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table: Table | undefined;

  materiales = signal<Material[]>([]);
  loading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  selectedRows: Material[] = [];
  filterValue: string = '';

  nroFilasPorPagina: number = environment.REGISTROS_PER_PAGE || 10;
  paginaActual = 0;
  paginate = true;
  rowsPerPageOptions: number[] = [];

  private destroy$ = new Subject<void>();
  private errorCount = 0;
  private readonly MAX_ERROR_COUNT = 3;
  private lastErrorTimestamp = 0;
  private readonly ERROR_RESET_TIMEOUT = 60000;

  // Mapas para almacenar rubros, tipos de material y tarifas por ID
  private rubrosMap: Map<string, any> = new Map();
  private tiposMaterialMap: Map<string, any> = new Map();
  private tarifasMap: Map<string, any> = new Map();

  columnsTable: Column[] = [
    { field: 'codigo', header: 'Código', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'nombre', header: 'Nombre', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'descripcion', header: 'Descripción', fieldType: 'text', sortable: true, exportable: false, filtrable: false },
    { field: 'rubro', header: 'Rubro', fieldType: 'text', sortable: false, exportable: false, filtrable: false },
    { field: 'tipoMaterial', header: 'Tipo', fieldType: 'text', sortable: false, exportable: false, filtrable: false },
    { field: 'tarifa', header: 'Tarifa', fieldType: 'text', sortable: false, exportable: false, filtrable: false },
    { field: 'frecuencia', header: 'Frecuencia', fieldType: 'text', sortable: true, exportable: false, filtrable: false },
    { field: 'costoDelSegundo', header: 'Costo/Seg', fieldType: 'number', sortable: true, exportable: true, filtrable: false },
    { field: 'accion', header: 'Acciones', sortable: false, filtrable: false }
  ];

  multiSortMeta: SortMeta[] = [];
  filtersMetadata: any = {};

  clearTableInProgress = false;
  clearTableCountLazyExecuted = 0;

  constructor(
    private materialService: MaterialService,
    private rubroService: RubroService,
    private tipoMaterialService: TipoMaterialService,
    private tarifaService: TarifaService,
    private router: Router,
    private messageService: MessageService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService
  ) {
    if (this.rowsPerPageOptions.length === 0) {
      this.rowsPerPageOptions = environment.REGISTROS_PER_PAGE_OPTIONS || [10, 20, 30, 50];
    }
  }

  ngOnInit(): void {
    this.loading.set(false);
    this.clearTableInProgress = false;
    this.clearTableCountLazyExecuted = 0;
    this.errorCount = 0;
    
    // Cargar rubros y tipos de material al inicializar
    this.cargarRubrosYTipos();
  }

  private cargarRubrosYTipos(): void {
    this.rubroService.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const rubros = resp['hydra:member'] || resp.member || resp;
          rubros.forEach((rubro: any) => {
            this.rubrosMap.set(`/api/rubros/${rubro.id}`, rubro);
          });
        },
        error: (error: any) => {
          console.warn('Error al cargar rubros:', error);
        }
      });

    this.tipoMaterialService.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const tipos = resp['hydra:member'] || resp.member || resp;
          tipos.forEach((tipo: any) => {
            this.tiposMaterialMap.set(`/api/tipo-materials/${tipo.id}`, tipo);
          });
        },
        error: (error: any) => {
          console.warn('Error al cargar tipos de material:', error);
        }
      });

    this.tarifaService.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          const tarifas = resp['hydra:member'] || resp.member || resp;
          tarifas.forEach((tarifa: any) => {
            this.tarifasMap.set(`/api/tarifas/${tarifa.id}`, tarifa);
          });
        },
        error: (error: any) => {
          console.warn('Error al cargar tarifas:', error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLazy(event: TableLazyLoadEvent): void {
    if (this.clearTableInProgress && this.clearTableCountLazyExecuted === 0) {
      this.clearTableCountLazyExecuted++;
      if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
        this.paginaActual = Math.floor(event.first / event.rows) + 1;
      } else {
        this.paginaActual = 1;
      }
      this.processLazyEvent(event);
      this.cargar();
    } else if (this.clearTableInProgress) {
      return;
    }

    this.processLazyEvent(event);
    this.cargar();
  }

  private processLazyEvent(event: TableLazyLoadEvent): void {
    if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
      this.paginaActual = Math.floor(event.first / event.rows) + 1;
      this.nroFilasPorPagina = event.rows;
    } else {
      this.paginaActual = 1;
      this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE ?? 10;
    }
    this.multiSortMeta = event.multiSortMeta || [];
    this.filterValue = typeof event.globalFilter === 'string' ? event.globalFilter : '';
    this.filtersMetadata = event.filters || {};
  }

  getFilterType(col: Column): string {
    if (!col.fieldType) return 'text';
    if (col.fieldType === 'date') return 'date';
    if (col.fieldType === 'boolean' || col.fieldType === 'boolean-icon') return 'boolean';
    if (col.fieldType === 'number') return 'numeric';
    return 'text';
  }

  getMatchMode(fieldType?: string): string {
    if (fieldType === 'boolean') return 'equals';
    if (fieldType === 'number') return 'equals';
    return 'contains';
  }

  cargar(): void {
    const now = Date.now();
    if (now - this.lastErrorTimestamp > this.ERROR_RESET_TIMEOUT) {
      this.errorCount = 0;
    }

    if (this.errorCount >= this.MAX_ERROR_COUNT) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Se alcanzó el límite de reintentos (${this.MAX_ERROR_COUNT}). Por favor, recarga la página o contacta al administrador.`,
        life: 8000,
        sticky: true
      });
      return;
    }

    this.loading.set(true);

    this.materialService.findAll(
      this.paginate,
      this.paginaActual,
      this.nroFilasPorPagina,
      this.filterValue || '',
      this.multiSortMeta,
      this.filtersMetadata
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.loading.set(false);
          this.errorCount = 0;
          this.lastErrorTimestamp = 0;

          const members = resp['hydra:member'] || resp.member || resp;

          const materialesMapeados = members.map((material: any) => ({
            id: material.id,
            codigo: material.codigo,
            nombre: material.nombre,
            descripcion: material.descripcion,
            frecuencia: material.frecuencia,
            costoDelSegundo: material.costoDelSegundo,
            activo: material.activo ?? true,
            tipoMaterial: material.tipoMaterial,
            rubro: material.rubro,
            tarifa: material.tarifa,
            selected: false
          }));

          this.materiales.set(materialesMapeados);
          this.totalRecords.set(resp['hydra:totalItems'] || resp.totalItems || materialesMapeados.length);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;
        },
        error: (error: any) => {
          this.loading.set(false);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;

          this.errorCount++;
          this.lastErrorTimestamp = Date.now();

          console.error(`Error al recuperar materiales (intento ${this.errorCount}/${this.MAX_ERROR_COUNT}):`, error);

          const errorDetail = this.errorCount >= this.MAX_ERROR_COUNT
            ? 'Se alcanzó el límite de reintentos. Por favor, recarga la página.'
            : `Ocurrió un error al recuperar materiales (intento ${this.errorCount}/${this.MAX_ERROR_COUNT})`;

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorDetail,
            life: this.errorCount >= this.MAX_ERROR_COUNT ? 8000 : 4000,
            sticky: this.errorCount >= this.MAX_ERROR_COUNT
          });

          this.filtersMetadata = {};
          this.multiSortMeta = [];
          this.materiales.set([]);
          this.totalRecords.set(0);
        }
      });
  }

  clear(table: Table): void {
    this.paginate = true;
    this.paginaActual = 0;
    this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE || 10;
    this.clearTableInProgress = true;
    this.filterValue = '';
    this.selectedRows = [];
    this.filtersMetadata = {};
    this.multiSortMeta = [];
    this.errorCount = 0;
    this.lastErrorTimestamp = 0;
    table.clear();
  }

  toggleMaterialEnabled(material: Material): void {
    if (!material.id) return;

    const nuevoEstado = material.activo ?? true;

    const materialActualizado: Material = {
      activo: nuevoEstado
    };

    this.loading.set(true);
    this.materialService.patch(material.id, materialActualizado)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: Material) => {
          this.loading.set(false);
          const estadoFinal = resp.activo ?? false;
          const accionTxt = estadoFinal ? 'activado' : 'desactivado';
          this.messageService.add({
            severity: 'success',
            summary: `Material ${accionTxt}`,
            detail: `El material ha sido ${accionTxt} correctamente.`,
            life: 3000
          });
          this.cargar();
        },
        error: (error: any) => {
          this.loading.set(false);
          material.activo = !nuevoEstado;
          console.error('Error al actualizar material:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar el estado del material',
            life: 3000
          });
        }
      });
  }

  editarMaterial(id: number): void {
    this.router.navigate(['/materiales/edit', id]);
  }

  navigateToCreateMaterial(): void {
    this.router.navigate(['/materiales/edit/nuevo']);
  }

  eliminarMaterial(material: Material): void {
    this.confirmationService.confirm({
      message: `¿Está seguro que desea desactivar el material ${material.nombre || ''}?`,
      header: 'Confirmar Desactivación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, desactivar',
      rejectLabel: 'Cancelar',
      accept: () => {
        if (!material.id) return;

        this.loading.set(true);
        const materialActualizado: Material = {
          activo: false
        };

        this.materialService.patch(material.id, materialActualizado)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.loading.set(false);
              this.messageService.add({
                severity: 'success',
                summary: 'Material Desactivado',
                detail: 'El material ha sido desactivado correctamente',
                life: 3000
              });
              this.cargar();
            },
            error: (error: any) => {
              this.loading.set(false);
              console.error('Error al desactivar material:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo desactivar el material',
                life: 3000
              });
            }
          });
      }
    });
  }

  activarMaterial(material: Material): void {
    if (!material.id) return;

    this.loading.set(true);
    const materialActualizado: Material = {
      activo: true
    };

    this.materialService.patch(material.id, materialActualizado)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Material Activado',
            detail: 'El material ha sido activado correctamente',
            life: 3000
          });
          this.cargar();
        },
        error: (error: any) => {
          this.loading.set(false);
          console.error('Error al activar material:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo activar el material',
            life: 3000
          });
        }
      });
  }

  getRubroDisplay(rubro: any): string {
    if (!rubro) return '-';
    
    // Si es un objeto con propiedad nombre
    if (typeof rubro === 'object' && rubro.nombre) {
      return rubro.nombre;
    }
    
    // Si es string (URL como /api/rubros/17), buscar en el mapa
    if (typeof rubro === 'string') {
      const rubroObj = this.rubrosMap.get(rubro);
      if (rubroObj) {
        return rubroObj.nombre || '-';
      }
      // Extraer número del final de la URL como fallback
      const match = rubro.match(/\/(\d+)$/);
      if (match) {
        return `Rubro ${match[1]}`;
      }
      return rubro;
    }
    return '-';
  }

  getTipoMaterialDisplay(tipoMaterial: any): string {
    if (!tipoMaterial) return '-';
    
    // Si es un objeto con propiedad nombre
    if (typeof tipoMaterial === 'object' && tipoMaterial.nombre) {
      return tipoMaterial.nombre;
    }
    
    // Si es string (URL como /api/tipo-materials/5), buscar en el mapa
    if (typeof tipoMaterial === 'string') {
      const tipoObj = this.tiposMaterialMap.get(tipoMaterial);
      if (tipoObj) {
        return tipoObj.nombre || '-';
      }
      // Extraer número del final de la URL como fallback
      const match = tipoMaterial.match(/\/(\d+)$/);
      if (match) {
        return `Tipo ${match[1]}`;
      }
      return tipoMaterial;
    }
    return '-';
  }

  getFrecuenciaDisplay(frecuencia: string | undefined): string {
    if (!frecuencia) return '-';
    const frecuencias: Record<string, string> = {
      'diaria': 'Diaria',
      'semanal': 'Semanal',
      'mensual': 'Mensual',
      'anual': 'Anual'
    };
    return frecuencias[frecuencia] || frecuencia;
  }

  getTarifaDisplay(tarifa: any): string {
    if (!tarifa) return '-';
    
    // Si es un objeto con propiedad nombre
    if (typeof tarifa === 'object' && tarifa.nombre) {
      return tarifa.nombre;
    }
    
    // Si es string (URL como /api/tarifas/5), buscar en el mapa
    if (typeof tarifa === 'string') {
      const tarifaObj = this.tarifasMap.get(tarifa);
      if (tarifaObj) {
        return tarifaObj.nombre || '-';
      }
      // Extraer número del final de la URL como fallback
      const match = tarifa.match(/\/(\d+)$/);
      if (match) {
        return `Tarifa ${match[1]}`;
      }
      return tarifa;
    }
    return '-';
  }
}
